import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { api, formatErr } from "@/lib/api";

export const MASTER_CFG = {
  vendors: {
    singular: "Vendor", endpoint: "/vendors",
    fields: [["name", "Vendor name", true], ["vat_number", "Vendor VAT registration number (15 digits)", true], ["contact_person", "Contact person"], ["mobile", "Mobile"], ["email", "Email"], ["address", "Address"]],
  },
  items: {
    singular: "Item", endpoint: "/items",
    fields: [["name", "Item name", true], ["code", "Item code (auto if blank)"], ["category", "Category"], ["description", "Description"]],
  },
};

export default function MasterDialog({ kind, open, onOpenChange, initial, presetName = "", presetValues = null, onSaved }) {
  const cfg = MASTER_CFG[kind];
  const blank = Object.fromEntries(cfg.fields.map(([k]) => [k, ""]));
  const [form, setForm] = useState({ ...blank, status: "active" });
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...blank, ...initial } : { ...blank, status: "active", ...(presetValues || {}), name: presetName });
      setDups(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async (confirm = false) => {
    setBusy(true);
    try {
      const body = { ...Object.fromEntries(cfg.fields.map(([k]) => [k, form[k] || ""])), status: form.status, confirm_duplicate: confirm };
      const { data } = initial ? await api.put(`${cfg.endpoint}/${initial.id}`, body) : await api.post(cfg.endpoint, body);
      toast.success(`${cfg.singular} ${initial ? "updated" : "added"}`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      if (e.response?.status === 409 && e.response.data.detail?.duplicates) setDups(e.response.data.detail.duplicates);
      else toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const missing = cfg.fields.some(([k, , req]) => req && !String(form[k] || "").trim());
  const onEnter = (e) => {
    if (e.key === "Enter" && e.target.tagName === "INPUT" && !missing && !busy) { e.preventDefault(); save(!!dups); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{initial ? `Edit ${cfg.singular}` : `Add new ${cfg.singular}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4" onKeyDown={onEnter}>
          {cfg.fields.map(([k, label, req]) => (
            <div key={k} className={k === "name" || k === "address" || k === "description" ? "col-span-2" : ""}>
              <Field label={label} required={req}>
                <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`${kind}-form-${k}`} className={k === "vat_number" ? "num" : ""} />
              </Field>
            </div>
          ))}
          {initial && (
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid={`${kind}-form-status`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </Field>
          )}
        </div>
        {dups && (
          <div className="border border-amber-200 bg-amber-50 rounded p-3 text-sm" data-testid={`${kind}-duplicate-warning`}>
            <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="w-4 h-4" />Possible duplicate</div>
            <ul className="mt-1.5 text-xs text-amber-900 space-y-0.5">
              {dups.map((d) => <li key={d.id}>{d.code} · {d.name} {d.vat_number && <span className="num">· {d.vat_number}</span>}</li>)}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {dups ? (
            <Button onClick={() => save(true)} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid={`${kind}-save-anyway`}>Save anyway</Button>
          ) : (
            <Button onClick={() => save(false)} disabled={busy || missing} className="bg-emerald-800 hover:bg-emerald-900" data-testid={`${kind}-form-submit`}>{busy ? "Saving…" : "Save"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
